import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect, useRef } from "react";

interface PasswordResetFormProps {
  email: string;
  setEmail: (email: string) => void;
  onSubmit: (e: React.FormEvent, captchaToken: string) => Promise<void>;
  isSubmitting: boolean;
  onBack: () => void;
}

export const PasswordResetForm = ({
  email,
  setEmail,
  onSubmit,
  isSubmitting,
  onBack,
}: PasswordResetFormProps) => {
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!turnstileRef.current || widgetIdRef.current) return;
    
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    
    if (!siteKey) {
      console.error('❌ VITE_TURNSTILE_SITE_KEY is not defined');
      setError('CAPTCHA configuration error. Please contact support.');
      return;
    }
    
    console.log('🔄 Waiting for Turnstile to load...');
    
    let attempts = 0;
    const maxAttempts = 50;
    
    const checkTurnstile = setInterval(() => {
      attempts++;
      
      if (window.turnstile) {
        clearInterval(checkTurnstile);
        console.log('✅ Turnstile loaded, rendering widget...');
        
        try {
          widgetIdRef.current = window.turnstile.render(turnstileRef.current!, {
            sitekey: siteKey,
            callback: (token: string) => {
              console.log('✅ CAPTCHA completed');
              setCaptchaToken(token);
              setError(null);
            },
          });
          console.log('✅ Widget rendered successfully');
        } catch (err) {
          console.error('❌ Failed to render Turnstile widget:', err);
          setError('Failed to load CAPTCHA. Please refresh the page.');
        }
      } else if (attempts >= maxAttempts) {
        clearInterval(checkTurnstile);
        console.error('❌ Turnstile script failed to load after 5 seconds');
        setError('CAPTCHA failed to load. Please refresh the page.');
      }
    }, 100);

    return () => {
      clearInterval(checkTurnstile);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!captchaToken) {
      setError("Please complete the CAPTCHA verification");
      return;
    }
    
    await onSubmit(e, captchaToken);
  };

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-6">
      <div className="space-y-2">
        <Input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="bg-primary border-secondary text-primary-foreground placeholder:text-muted-foreground"
        />
      </div>
      <div ref={turnstileRef} />
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
      <div className="space-y-4">
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
        >
          {isSubmitting ? "Requesting..." : "Reset Password"}
        </Button>
        <Button
          type="button"
          disabled={isSubmitting}
          variant="ghost"
          className="w-full"
          onClick={onBack}
        >
          Back to Sign In
        </Button>
      </div>
    </form>
  );
};